import "../../../assets/css/PremiumTools.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Search,
  Copy,
  RefreshCw,
  Zap,
  Globe,
  CheckCircle,
  AlertCircle,
  Layout,
  MousePointer2,
  ArrowLeft
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init"; 

const MetaGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [url, setUrl] = useState("");
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("SEO Optimized (High CTR)");

  // Generated Content State (Array of options)
  const [results, setResults] = useState([]);

  // Options
  const toneOptions = [
    "SEO Optimized (High CTR)",
    "Professional / Corporate",
    "Creative / Viral",
    "Question Based (Educational)"
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (UNCHANGED)
  // ==========================================
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        setApiKeys(response.data.data.map((keyObj) => keyObj.key));
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error(err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        }
      } catch (err) {
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are a world-class SEO Expert. Generate 3 distinct sets of Meta Titles and Meta Descriptions based on the following:

    Topic/Context: ${topic}
    ${url ? `Target URL: ${url}` : ""}
    Target Keywords: ${keywords}
    Style/Tone: ${tone}

    STRICT CONSTRAINTS:
    1. **Title:** Must be between 55-60 characters (including spaces). Urgent and clickable.
    2. **Description:** Must be between 130-155 characters. clearly summarizing the page.
    3. Include the target keyword naturally.
    
    OUTPUT FORMAT:
    You must output strictly in this JSON format (no markdown code blocks):
    [
      {"title": "Title 1 here", "desc": "Description 1 here"},
      {"title": "Title 2 here", "desc": "Description 2 here"},
      {"title": "Title 3 here", "desc": "Description 3 here"}
    ]`;
  };

  const generateMeta = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic or page context.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits.");
      return;
    }

    setGenerating(true);
    setError("");
    setResults([]);

    try {
      // 1. Deduct Credit
      const deductResponse = await axios.post(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/deduct-credit`,
        { userEmail: user.email, amount: 1 }
      );

      if (!deductResponse.data.success) throw new Error("Failed to deduct credits");
      setCredits((prev) => prev - 1);

      // 2. Call AI
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 1024 },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;

      // Parse JSON Output
      try {
        // Cleanup markdown if AI adds it
        const cleanJson = textResult.replace(/```json/g, '').replace(/```/g, '').trim();
        const parsed = JSON.parse(cleanJson);
        if (Array.isArray(parsed)) {
          setResults(parsed);
          toast.success("Meta tags generated!");
        } else {
          throw new Error("Invalid format");
        }
      } catch (parseError) {
        console.error("Parsing failed", textResult);
        throw new Error("AI produced invalid format. Please try again.");
      }

    } catch (err) {
      console.error(err);
      toast.error(err.message);
      setError(err.message);

      // Refund Logic
      try {
        await axios.post(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/refund-credit`, {
          userEmail: user.email, amount: 1,
        });
        setCredits((prev) => prev + 1);
        toast("Credits refunded.");
      } catch (refundErr) { console.error(refundErr); }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS & UI HELPERS
  // ==========================================
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied!"));
  };

  // Character Count Color Logic
  const getLenColor = (len, type) => {
    if (type === 'title') {
      if (len > 60) return 'text-danger'; // Too long
      if (len < 30) return 'text-warning'; // Too short
      return 'text-success'; // Perfect
    } else {
      if (len > 160) return 'text-danger';
      if (len < 100) return 'text-warning';
      return 'text-success';
    }
  };

  // ==========================================
  // 5. RENDER UI
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="d-flex justify-content-center align-items-center bg-dark" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wall d-flex align-items-center justify-content-center" style={{ height: "100vh", background: "#0f172a" }}>
        <div className="glass-card p-5 text-center" style={{ maxWidth: "400px", width: "90%" }}>
          <div className="mb-4 mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width: 60, height: 60}}>
            <Search size={30} className="text-neon" />
          </div>
          <h2 className="fw-bold text-white mb-2">SEO Meta Generator</h2>
          <p className="text-light opacity-75 mb-4">Log in to optimize your search rankings.</p>
          <button className="btn btn-neon-primary btn-lg w-100 fw-semibold" onClick={() => window.location.href = "/login"}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-app-container">
      {/* Background Orbs */}
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      {/* Main Content */}
      <div className="container py-5 position-relative z-1">
        
        {/* Header Row */}
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-light rounded-circle p-2 back-btn d-flex align-items-center justify-content-center" onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </button>
            <h1 className="fw-bold m-0 text-white">
              SEO Meta <span className="text-neon">Generator</span>
            </h1>
          </div>

          <div className="credits-badge">
            <Zap size={16} fill="#eab308" color="#eab308" />
            <span>{credits} Credits</span>
            {credits < 5 && (
              <button className="btn-buy-credits" onClick={() => window.location.href = "/user/select-package"}>
                Buy More
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger glass-alert">{error}</div>}

        <div className="row g-4">
          {/* LEFT SIDEBAR - FORM */}
          <div className="col-lg-4">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <Layout size={18} className="text-neon" />
                  Page Details
                </h5>
              </div>

              <div className="card-body-glass flex-grow-1 d-flex flex-column overflow-auto">
                {/* Topic Input */}
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Topic / Page Content</label>
                  <textarea 
                    className="form-control input-group-glass" 
                    rows="4" 
                    placeholder="e.g. A blog post about the benefits of green tea for weight loss..."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    style={{ resize: "none" }}
                  />
                </div>

                {/* Keywords */}
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Target Keywords</label>
                  <input 
                    type="text"
                    className="form-control input-group-glass"
                    placeholder="e.g. green tea benefits, weight loss"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                  />
                </div>

                {/* Tone */}
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Tone Strategy</label>
                  <select 
                    className="form-select input-group-glass"
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                  >
                    {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Generate Button */}
                <div className="mt-auto pt-3">
                  <button 
                    className="btn btn-neon-primary w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={generateMeta}
                    disabled={generating || credits < 1 || !topic.trim()}
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="spin" size={18} /> Optimizing...
                      </>
                    ) : (
                      <>
                        <Zap size={18} /> Generate (1 Credit)
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR - OUTPUT */}
          <div className="col-lg-8">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass d-flex justify-content-between align-items-center">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <MousePointer2 size={18} className={results.length > 0 ? "text-success" : "text-neon"} />
                  Google Search Preview
                </h5>
                {results.length > 0 && <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">3 Variations Generated</span>}
              </div>

              <div className="card-body-glass flex-grow-1 overflow-auto position-relative" style={{ minHeight: "500px" }}>
                {generating ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-75 py-5">
                    <div className="spinner-border text-neon mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-white">Analyzing SERP...</h5>
                    <p className="small">Checking lengths and keyword density.</p>
                  </div>
                ) : results.length > 0 ? (
                  <div className="d-flex flex-column gap-4 h-100 p-2">
                    {results.map((item, idx) => (
                      <div key={idx} className="glass-card mb-2 p-4 border border-light border-opacity-10">
                        {/* Google Simulator View */}
                        <div className="google-preview mb-4 bg-white rounded p-3 text-dark">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <div className="d-flex align-items-center justify-content-center bg-light rounded-circle" style={{ width: '26px', height: '26px' }}>
                              {url ? <img src={`https://www.google.com/s2/favicons?domain=${url}`} alt="" style={{ width: '16px', height: '16px' }} /> : <Globe size={14} className="text-secondary" />}
                            </div>
                            <div className="d-flex flex-column lh-1">
                              <span className="text-dark" style={{ fontSize: '14px' }}>{url ? new URL('http://'+url.replace('http://','').replace('https://','')).hostname : 'YourSite.com'}</span>
                              <span className="text-secondary" style={{ fontSize: '12px' }}>{url || 'https://www.yoursite.com/slug'}</span>
                            </div>
                          </div>
                          <div style={{ fontSize: '20px', color: '#1a0dab', cursor: 'pointer', lineHeight: '1.3', marginBottom: '3px' }}>{item.title}</div>
                          <div style={{ fontSize: '14px', color: '#4d5156', lineHeight: '1.58' }}>{item.desc}</div>
                        </div>

                        {/* Controls & Metrics */}
                        <div className="d-flex flex-column gap-3 p-3 bg-dark bg-opacity-50 rounded">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-3 flex-grow-1 me-3">
                              <span className="text-light opacity-75 text-uppercase fw-bold" style={{ fontSize: '11px', width: '40px' }}>Title</span>
                              <div className="flex-grow-1 bg-secondary bg-opacity-50 rounded-pill" style={{ height: '6px', maxWidth: '150px' }}>
                                <div 
                                  className={`h-100 rounded-pill ${getLenColor(item.title.length, 'title').replace('text-', 'bg-')}`} 
                                  style={{width: `${Math.min((item.title.length / 70) * 100, 100)}%`, transition: 'width 0.3s'}}
                                ></div>
                              </div>
                              <span className={`fw-semibold text-end ${getLenColor(item.title.length, 'title')}`} style={{ fontSize: '11px', minWidth: '60px' }}>{item.title.length} / 60</span>
                            </div>
                            <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-2" onClick={() => copyToClipboard(item.title)}>
                              <Copy size={12} /> Title
                            </button>
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-3 flex-grow-1 me-3">
                              <span className="text-light opacity-75 text-uppercase fw-bold" style={{ fontSize: '11px', width: '40px' }}>Desc</span>
                              <div className="flex-grow-1 bg-secondary bg-opacity-50 rounded-pill" style={{ height: '6px', maxWidth: '150px' }}>
                                <div 
                                  className={`h-100 rounded-pill ${getLenColor(item.desc.length, 'desc').replace('text-', 'bg-')}`} 
                                  style={{width: `${Math.min((item.desc.length / 160) * 100, 100)}%`, transition: 'width 0.3s'}}
                                ></div>
                              </div>
                              <span className={`fw-semibold text-end ${getLenColor(item.desc.length, 'desc')}`} style={{ fontSize: '11px', minWidth: '60px' }}>{item.desc.length} / 160</span>
                            </div>
                            <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-2" onClick={() => copyToClipboard(item.desc)}>
                              <Copy size={12} /> Desc
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-25 py-5">
                    <Search size={64} className="mb-3" />
                    <h3 className="fs-4 mb-2 text-white opacity-50">No Previews Yet</h3>
                    <p className="text-center px-4">Enter your page details to visualize search results.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      
    </div>
  );
};

export default MetaGenerator;
